/* @img-migrated */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, RefreshCw, Search, X, Loader2, Upload, ExternalLink,
  Gift, Star, Calendar, Building2, User, AlertTriangle, Tag, Image as ImageIcon,
  ChevronRight, CheckCircle2, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  'Professional Services', 'Food & Restaurants', 'Retail', 'Health & Wellness',
  'Beauty & Personal Care', 'Home & Services', 'Legal & Financial',
  'Marketing & Creative', 'Technology', 'Travel & Hospitality',
  'Education & Training', 'Other',
];

const STATUS_PILL = {
  draft:     'bg-amber-100 text-amber-700 border-amber-200',
  scheduled: 'bg-sky-100 text-sky-700 border-sky-200',
  active:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired:   'bg-slate-200 text-slate-500 border-slate-300',
  paused:    'bg-violet-100 text-violet-700 border-violet-200',
};

const STATUS_FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'active',    label: 'Active' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'paused',    label: 'Paused' },
  { id: 'expired',   label: 'Expired' },
  { id: 'draft',     label: 'Draft' },
];

const blankDeal = () => ({
  title: '',
  description: '',
  category: 'Professional Services',
  image_url: '',
  cta_text: 'Redeem Offer',
  cta_url: '',
  contact_method: '',
  assigned_user_id: null,
  assigned_user: null,
  assigned_company_name: '',
  start_at: '',
  end_at: '',
  active: true,
  featured: false,
});

// ---- Section wrapper for a numbered editor block ----------------------------
const Section = ({ n, title, hint, children, testId }) => (
  <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={testId}>
    <div className="px-5 py-3 bg-slate-50/70 border-b border-slate-200 flex items-center gap-3">
      <span className="w-6 h-6 rounded-full bg-[#002249] text-white text-[12px] font-bold flex items-center justify-center">{n}</span>
      <div>
        <h3 className="text-[13px] font-bold text-[#002249]">{title}</h3>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
    </div>
    <div className="p-5 space-y-3">{children}</div>
  </div>
);

const AdminMemberDealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Legacy migration state
  const [legacyScan, setLegacyScan] = useState(null); // { unmigrated_count, unmigrated, ... }
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  // Lock body scroll while editor modal is open
  useBodyScrollLock(editorOpen);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dealsRes, pipeRes, scanRes] = await Promise.all([
        axios.get(`${API}/admin/member-deals`, { withCredentials: true }),
        axios.get(`${API}/admin/member-deals/pipeline`, { withCredentials: true }),
        axios.get(`${API}/admin/member-deals/legacy-scan`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setDeals(dealsRes.data.deals || []);
      setPipeline(pipeRes.data || null);
      setLegacyScan(scanRes.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const runLegacyMigration = async () => {
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/admin/member-deals/migrate-legacy`, {}, { withCredentials: true });
      setMigrationResult(r.data);
      const created = r.data?.created || 0;
      const skipped = (r.data?.skipped_already_migrated || 0) + (r.data?.skipped_empty_description || 0);
      toast.success(`${created} legacy deal${created === 1 ? '' : 's'} imported${skipped ? ` · ${skipped} skipped` : ''}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const filtered = useMemo(() => {
    let list = deals;
    if (filter !== 'all') list = list.filter((d) => d.status === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((d) =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.assigned_company_name || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [deals, filter, search]);

  const openNew = () => { setEditing(blankDeal()); setEditorOpen(true); };
  const openEdit = (d) => {
    setEditing({
      ...d,
      start_at: d.start_at ? d.start_at.slice(0, 16) : '',
      end_at: d.end_at ? d.end_at.slice(0, 16) : '',
    });
    setEditorOpen(true);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/member-deals/${d.id}`, { withCredentials: true });
      toast.success('Deal deleted');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete deal');
    }
  };

  const togglePause = async (d) => {
    try {
      await axios.put(`${API}/admin/member-deals/${d.id}`, { active: !d.active }, { withCredentials: true });
      toast.success(d.active ? 'Deal paused' : 'Deal activated');
      fetchAll();
    } catch (err) {
      toast.error('Could not update deal');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-member-deals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider mb-2">
            <Gift className="w-3 h-3" /> Member Deals · CRUD
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Member Deals Management</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Create, schedule, and feature member-to-member offers. Active deals appear in the Member Deals
            directory for tier-eligible members.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAll} className="h-10 rounded-lg" data-testid="deals-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={openNew} className="h-10 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="deals-new">
            <Plus className="w-4 h-4 mr-1.5" /> New Deal
          </Button>
        </div>
      </div>

      {/* Pipeline */}
      <PipelineStrip pipeline={pipeline} loading={loading} />

      {/* Legacy migration banner — shown only when there are unmigrated profile-based deals */}
      {legacyScan && legacyScan.unmigrated_count > 0 && (
        <div
          className="mt-4 p-4 sm:p-5 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="legacy-migration-banner"
        >
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-amber-900 text-[14.5px] tracking-tight">
              {legacyScan.unmigrated_count} legacy deal{legacyScan.unmigrated_count === 1 ? '' : 's'} not yet imported
            </p>
            <p className="text-[12.5px] text-amber-800/85 leading-relaxed mt-0.5">
              Members ticked &ldquo;I offer a discount&rdquo; on their company profile before the new system existed.
              Import them so Admin becomes the single source of truth — company name, logo, category, description,
              and contact method are all preserved.
            </p>
          </div>
          <Button
            onClick={runLegacyMigration}
            disabled={migrating}
            className="h-10 rounded-lg bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
            data-testid="legacy-migrate-btn"
          >
            {migrating ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</>
            ) : (
              <><Download className="w-4 h-4 mr-1.5" /> Import {legacyScan.unmigrated_count} legacy deal{legacyScan.unmigrated_count === 1 ? '' : 's'}</>
            )}
          </Button>
        </div>
      )}

      {/* Migration result banner — shown briefly after migration */}
      {migrationResult && migrationResult.created > 0 && (
        <div
          className="mt-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50 flex items-center gap-2 text-[13px]"
          data-testid="legacy-migration-result"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="flex-1 text-emerald-900">
            <strong>{migrationResult.created}</strong> deal{migrationResult.created === 1 ? '' : 's'} imported successfully
            {migrationResult.skipped_already_migrated > 0 && ` · ${migrationResult.skipped_already_migrated} already imported`}
            {migrationResult.skipped_empty_description > 0 && ` · ${migrationResult.skipped_empty_description} skipped (no description)`}
            {migrationResult.errors?.length > 0 && ` · ${migrationResult.errors.length} error${migrationResult.errors.length === 1 ? '' : 's'}`}
          </span>
          <button onClick={() => setMigrationResult(null)} className="text-emerald-700 hover:text-emerald-900 text-[12px] font-semibold underline" data-testid="legacy-migration-result-dismiss">
            Dismiss
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, description, or company..."
            className="h-10 pl-9 rounded-lg"
            data-testid="deals-search"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="deals-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`deals-filter-${f.id}`}
              className={`px-3 h-9 rounded-lg text-[12px] font-semibold whitespace-nowrap transition-colors ${
                filter === f.id ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#002249]/40'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Deals list */}
      <div className="mt-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="h-44 bg-white border border-slate-200 rounded-xl skeleton" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
            <Gift className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {deals.length === 0 ? 'No deals created yet — start with your first one.' : 'No deals match your filters.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="deals-grid">
            {filtered.map((d) => (
              <DealRow key={d.id} d={d} onEdit={openEdit} onDelete={remove} onTogglePause={togglePause} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {editorOpen && editing && (
          <DealEditor
            initial={editing}
            onClose={() => setEditorOpen(false)}
            onSaved={() => { setEditorOpen(false); fetchAll(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ---- Pipeline strip ---------------------------------------------------------
const PipelineStrip = ({ pipeline, loading }) => {
  const counts = pipeline?.counts || { total: 0, active: 0, scheduled: 0, expired: 0, paused: 0, draft: 0, featured: 0 };
  const items = [
    { id: 'total',     label: 'Total deals', value: counts.total,     color: 'text-[#002249]' },
    { id: 'active',    label: 'Active now',  value: counts.active,    color: 'text-emerald-600' },
    { id: 'scheduled', label: 'Scheduled',   value: counts.scheduled, color: 'text-sky-600' },
    { id: 'paused',    label: 'Paused',      value: counts.paused,    color: 'text-violet-600' },
    { id: 'expired',   label: 'Expired',     value: counts.expired,   color: 'text-slate-500' },
    { id: 'featured',  label: 'Featured',    value: counts.featured,  color: 'text-amber-600' },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="deals-pipeline">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`deals-pipeline-${it.id}`}>
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{it.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${it.color}`}>{loading ? '…' : it.value}</p>
          </div>
        ))}
      </div>
      {(pipeline?.expiring_soon?.length || 0) > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid="deals-expiring-soon">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Expiring within 7 days
          </p>
          <ul className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-1">
            {pipeline.expiring_soon.map((e) => (
              <li key={e.id} className="text-[12px] text-slate-700 flex items-center justify-between gap-2">
                <span className="truncate">{e.title} {e.company_name ? <span className="text-slate-400">· {e.company_name}</span> : null}</span>
                <span className="text-[10.5px] text-slate-400 font-mono">{(e.end_at || '').slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---- Deal row card ----------------------------------------------------------
const DealRow = ({ d, onEdit, onDelete, onTogglePause }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#002249]/40 transition-colors" data-testid={`deal-card-${d.id}`}>
    <div className="flex">
      <div className="w-24 h-24 bg-slate-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
        {d.image_url ? (
          <Img variant="generic" src={resolveImg(d.image_url)} alt={d.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Gift className="w-7 h-7 text-slate-300" />
        )}
      </div>
      <div className="p-3 flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_PILL[d.status] || STATUS_PILL.draft}`} data-testid={`deal-status-${d.id}`}>
            {d.status}
          </span>
          {d.featured && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold uppercase">
              <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" /> Featured
            </span>
          )}
        </div>
        <h3 className="text-[13.5px] font-bold text-[#002249] truncate" title={d.title}>{d.title || '(Untitled deal)'}</h3>
        <p className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">{d.description}</p>
        <div className="flex items-center gap-3 mt-1.5 text-[10.5px] text-slate-400">
          {d.category && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" />{d.category}</span>}
          {d.assigned_company_name && <span className="inline-flex items-center gap-1 truncate"><Building2 className="w-3 h-3" />{d.assigned_company_name}</span>}
          {d.assigned_user?.name && <span className="inline-flex items-center gap-1 truncate"><User className="w-3 h-3" />{d.assigned_user.name}</span>}
        </div>
      </div>
    </div>
    <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
      <div className="text-[10.5px] text-slate-400 font-mono">
        {d.end_at ? <><Calendar className="w-3 h-3 inline mr-1" />ends {d.end_at.slice(0, 10)}</> : 'no end date'}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onTogglePause(d)} className="h-7 px-2 rounded-md text-[11px] text-slate-600 hover:bg-white border border-transparent hover:border-slate-200" data-testid={`deal-toggle-${d.id}`}>
          {d.active ? 'Pause' : 'Activate'}
        </button>
        <button onClick={() => onEdit(d)} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-600 hover:bg-white hover:text-[#002249]" data-testid={`deal-edit-${d.id}`}>
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(d)} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-600 hover:bg-red-50 hover:text-red-600" data-testid={`deal-delete-${d.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
);

// ---- Editor -----------------------------------------------------------------
const DealEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const status = useMemo(() => {
    if (!form.title || !form.description) return 'draft';
    if (form.end_at && new Date(form.end_at) < new Date()) return 'expired';
    if (!form.active) return 'paused';
    if (form.start_at && new Date(form.start_at) > new Date()) return 'scheduled';
    return 'active';
  }, [form.title, form.description, form.end_at, form.start_at, form.active]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files allowed');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/member-deals/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set({ image_url: res.data.url });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      image_url: form.image_url || '',
      cta_text: form.cta_text || '',
      cta_url: form.cta_url || '',
      contact_method: form.contact_method || '',
      assigned_user_id: form.assigned_user_id || null,
      assigned_company_name: form.assigned_company_name || '',
      start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
      end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      active: !!form.active,
      featured: !!form.featured,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/admin/member-deals/${form.id}`, payload, { withCredentials: true });
        toast.success('Deal updated');
      } else {
        await axios.post(`${API}/admin/member-deals`, payload, { withCredentials: true });
        toast.success('Deal created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <motion.div
        className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
      <motion.div
        initial={{ y: 20, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 12, scale: 0.98 }}
        className="bg-slate-50 w-full sm:max-w-3xl sm:rounded-2xl overflow-hidden flex flex-col h-full sm:max-h-[90vh]"
        data-testid="deal-editor"
      >
        <div className="px-5 py-3.5 bg-[#002249] text-white flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-white/60">{form.id ? 'Edit Deal' : 'New Member Deal'}</p>
            <h2 className="text-[15.5px] font-bold tracking-tight">{form.title || 'Untitled deal'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase ${STATUS_PILL[status]}`} data-testid="deal-status-preview">
              {status}
            </span>
            <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center" data-testid="deal-editor-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
          <Section n={1} title="Basic info" hint="What the deal is and the offering category." testId="deal-section-basic">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="20% off all professional services" maxLength={160} data-testid="deal-title" />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="Describe what's included, eligibility, and any restrictions..."
                rows={4}
                maxLength={2000}
                className="w-full p-3 border border-slate-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                data-testid="deal-description"
              />
            </div>
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => set({ category: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-md text-[13px] bg-white" data-testid="deal-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </Section>

          <Section n={2} title="Cover image" hint="Single hero image shown on the deal card. JPG/PNG/WEBP up to 10 MB." testId="deal-section-image">
            <div className="flex items-start gap-3">
              <div className="w-32 h-24 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {form.image_url ? (
                  <Img variant="generic" src={resolveImg(form.image_url)} alt="cover" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleUpload} className="hidden" data-testid="deal-image-input" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-lg" data-testid="deal-image-upload">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {form.image_url ? 'Replace image' : 'Upload image'}
                </Button>
                {form.image_url && (
                  <Button type="button" variant="ghost" onClick={() => set({ image_url: '' })} className="rounded-lg text-[12px] text-slate-500 hover:text-red-600 ml-2" data-testid="deal-image-clear">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </Section>

          <Section n={3} title="Audience & assignment" hint="Optionally tie this deal to a member or company name." testId="deal-section-audience">
            <MemberSearch
              value={form.assigned_user}
              onChange={(u) => set({ assigned_user: u, assigned_user_id: u?.id || null, assigned_company_name: u?.company_name || form.assigned_company_name })}
            />
            <div>
              <Label>Company name (display)</Label>
              <Input value={form.assigned_company_name} onChange={(e) => set({ assigned_company_name: e.target.value })} placeholder="Visible label on the deal card" data-testid="deal-company-name" />
            </div>
          </Section>

          <Section n={4} title="Call to action" hint="Primary button members tap to redeem." testId="deal-section-cta">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Button label</Label>
                <Input value={form.cta_text} onChange={(e) => set({ cta_text: e.target.value })} placeholder="Redeem Offer" data-testid="deal-cta-text" />
              </div>
              <div>
                <Label>Link URL</Label>
                <Input value={form.cta_url} onChange={(e) => set({ cta_url: e.target.value })} placeholder="https://..." data-testid="deal-cta-url" />
              </div>
            </div>
            <div>
              <Label>How to redeem (optional)</Label>
              <Input value={form.contact_method} onChange={(e) => set({ contact_method: e.target.value })} placeholder='e.g., "Mention DCCF code DC20"' data-testid="deal-contact-method" />
            </div>
          </Section>

          <Section n={5} title="Schedule & status" hint="Control when the deal is visible to members." testId="deal-section-schedule">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Start (optional)</Label>
                <Input type="datetime-local" value={form.start_at || ''} onChange={(e) => set({ start_at: e.target.value })} data-testid="deal-start" />
              </div>
              <div>
                <Label>End (optional)</Label>
                <Input type="datetime-local" value={form.end_at || ''} onChange={(e) => set({ end_at: e.target.value })} data-testid="deal-end" />
              </div>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <label className="inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer" data-testid="deal-active-toggle">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set({ active: e.target.checked })} className="w-4 h-4 rounded border-slate-300" />
                Active (visible to members)
              </label>
              <label className="inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer" data-testid="deal-featured-toggle">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => set({ featured: e.target.checked })} className="w-4 h-4 rounded border-slate-300" />
                Featured (pinned to top)
              </label>
            </div>
          </Section>
        </div>

        <div className="px-5 py-3.5 bg-white border-t border-slate-200 flex items-center justify-between flex-shrink-0">
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-lg" data-testid="deal-editor-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={saving} className="rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white px-5" data-testid="deal-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            {form.id ? 'Save changes' : 'Create deal'}
          </Button>
        </div>
      </motion.div>
      </motion.div>
    </ModalPortal>
  );
};

// ---- Member search (autocomplete) ------------------------------------------
const MemberSearch = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get(`${API}/admin/member-deals/access/search-members`, {
          params: { q },
          withCredentials: true,
        });
        setResults(res.data.results || []);
      } catch { /* */ } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <Label>Assign to a member (optional)</Label>
      {value ? (
        <div className="flex items-center justify-between gap-3 px-3 h-10 rounded-md border border-slate-200 bg-slate-50" data-testid="deal-assigned-member">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[11px] text-slate-500">
              {value.picture ? <Img variant="avatar" src={value.picture} alt="" className="w-full h-full object-cover" /> : (value.name || '?').slice(0, 1)}
            </div>
            <span className="text-[12.5px] text-[#002249] font-semibold truncate">{value.name}</span>
            <span className="text-[11px] text-slate-400 truncate">· {value.email}</span>
          </div>
          <button type="button" onClick={() => onChange(null)} className="text-[11px] text-slate-500 hover:text-red-600" data-testid="deal-clear-member">
            Clear
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              placeholder="Search by name or email..."
              className="h-10 pl-9 rounded-md"
              data-testid="deal-member-search"
            />
          </div>
          {open && q && q.length >= 2 && (
            <div className="absolute left-0 right-0 mt-1 z-10 bg-white border border-slate-200 rounded-md shadow-md max-h-56 overflow-y-auto">
              {busy ? (
                <div className="p-3 text-[12px] text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Searching…</div>
              ) : results.length === 0 ? (
                <div className="p-3 text-[12px] text-slate-500">No matches</div>
              ) : (
                results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { onChange(u); setQ(''); setOpen(false); }}
                    className="w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 text-left"
                    data-testid={`deal-member-result-${u.id}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[11px] text-slate-500 flex-shrink-0">
                      {u.picture ? <Img variant="avatar" src={u.picture} alt="" className="w-full h-full object-cover" /> : (u.name || '?').slice(0, 1)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-[#002249] truncate">{u.name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminMemberDealsPage;
